import { useState } from "react";
import { Phone, Mail, Instagram, MapPin, Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "../components/Reveal";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useContent from "../lib/useContent";
import api, { formatApiError } from "../lib/api";

const TalkToUs = () => {
  const content = useContent();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Fill in all three fields and it'll send.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — thank you! I'll get back to you after school.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const rows = [
    { icon: Mail, label: "Email", value: content.contact_email },
    { icon: Instagram, label: "Instagram", value: content.contact_instagram },
    { icon: MapPin, label: "Based in", value: content.contact_location },
  ];

  return (
    <div className="min-h-screen bg-cream dark:bg-background text-foreground">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-12 md:py-16">
        <Reveal>
          <Eyebrow>Talk to us</Eyebrow>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter">
            Talk to me — <span className="font-serif italic font-medium marker-highlight">seriously</span>
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">{content.talk_intro}</p>
        </Reveal>

        <Reveal delay={0.08}>
          <div
            data-testid="talk-phone-card"
            className="mt-10 rounded-3xl bg-pitch dark:bg-card text-cream p-8 md:p-10 relative overflow-hidden shadow-xl shadow-pitch/25"
          >
            <div className="absolute -right-10 -bottom-12 opacity-10" aria-hidden="true">
              <MessageCircle className="h-52 w-52" />
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-turf">
              Call, text or WhatsApp
            </p>
            <p data-testid="talk-phone-number" className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight break-words">
              {content.contact_phone}
            </p>
            <p className="mt-4 text-cream/75 text-sm sm:text-base max-w-md leading-relaxed">
              Ask me anything about the research, send your story, or invite me to count your team's
              players for the survey. I reply after school — promise.
            </p>
          </div>
        </Reveal>

        <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="talk-contact-rows">
          {rows.map((r, idx) => (
            <Reveal key={r.label} delay={0.05 * idx}>
              <div className="flex items-start gap-3 rounded-2xl bg-card border border-foreground/10 px-5 py-4">
                <span className="h-10 w-10 rounded-full bg-sage/50 dark:bg-turf/15 grid place-items-center shrink-0">
                  <r.icon className="h-5 w-5 text-pitch dark:text-turf" />
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{r.label}</p>
                  <p className="font-semibold text-sm break-words">{r.value}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <form
            data-testid="talk-form"
            onSubmit={submit}
            className="mt-10 rounded-3xl bg-card border-2 border-foreground/10 p-6 md:p-8 shadow-sm space-y-4"
          >
            <h2 className="font-display text-2xl font-bold tracking-tight">Or leave a message here</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="talk-name" className="text-sm font-semibold">Your name</label>
                <input
                  id="talk-name"
                  data-testid="talk-input-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="e.g. Sam from the U16s"
                  className="mt-2 w-full h-12 rounded-2xl border-2 border-foreground/10 bg-background px-4 text-sm focus:outline-none focus:border-pitch dark:focus:border-turf"
                />
              </div>
              <div>
                <label htmlFor="talk-email" className="text-sm font-semibold">Your email</label>
                <input
                  id="talk-email"
                  type="email"
                  data-testid="talk-input-email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="so I can reply"
                  className="mt-2 w-full h-12 rounded-2xl border-2 border-foreground/10 bg-background px-4 text-sm focus:outline-none focus:border-pitch dark:focus:border-turf"
                />
              </div>
            </div>
            <div>
              <label htmlFor="talk-message" className="text-sm font-semibold">Your message</label>
              <textarea
                id="talk-message"
                data-testid="talk-input-message"
                value={form.message}
                onChange={set("message")}
                rows={5}
                placeholder="Hi! I'd love to know…"
                className="mt-2 w-full rounded-2xl border-2 border-foreground/10 bg-background px-4 py-3 text-sm focus:outline-none focus:border-pitch dark:focus:border-turf resize-y"
              />
            </div>
            <button
              type="submit"
              data-testid="talk-submit-btn"
              disabled={submitting}
              className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send message
            </button>
          </form>
        </Reveal>
      </main>
      <Footer />
    </div>
  );
};

export default TalkToUs;
